import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { userAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { User, Trophy, Award, Target, Flame, Edit2, Save, X, CreditCard, Gift, Trash2, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '../../lib/apiErrors';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { LegalLinks } from '../../components/LegalLinks';

export function ProfilePage() {
  const { user, updateUser, logout } = useAuth();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [newNickname, setNewNickname] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const [profileRes, badgesRes] = await Promise.all([
        userAPI.getProfile(),
        userAPI.getMyBadges()
      ]);
      setProfile(profileRes.data);
      setBadges(badgesRes.data.badges || []);
      setNewNickname(profileRes.data.nickname || '');
      setFirstName(profileRes.data.first_name || '');
      setLastName(profileRes.data.last_name || '');
      setBankAccount(profileRes.data.bank_account || '');
    } catch (error) {
      toast.error(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNickname = async () => {
    if (!newNickname.trim() || newNickname === profile.nickname) {
      setEditing(false);
      return;
    }

    setSaving(true);
    try {
      await userAPI.updateProfile({ nickname: newNickname.trim() });
      setProfile({ ...profile, nickname: newNickname.trim() });
      updateUser({ ...user, nickname: newNickname.trim() });
      toast.success(t('profile.updated'));
      setEditing(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, t, 'errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const DELETE_WORD = 'DELETE';

  const handleDeleteAccount = async () => {
    if (deleteConfirm.trim().toUpperCase() !== DELETE_WORD) return;
    setDeleting(true);
    try {
      await userAPI.deleteAccount();
      setDeleteOpen(false);
      logout();
      toast.success(t('profile.deleteAccount.done'));
      navigate('/', { replace: true });
    } catch (error) {
      toast.error(apiErrorMessage(error, t, 'profile.deleteAccount.failed'));
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveDetails = async () => {
    setSaving(true);
    try {
      await userAPI.updateProfile({ 
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        bank_account: bankAccount.trim()
      });
      setProfile({ 
        ...profile, 
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        bank_account: bankAccount.trim()
      });
      toast.success(t('profile.updated'));
      setEditingDetails(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, t, 'errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-400">{t('profile.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Profile Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50 text-center"
      >
        {/* Avatar */}
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center mx-auto mb-4 text-4xl font-bold text-white shadow-lg shadow-teal-500/30">
          {profile.nickname?.charAt(0).toUpperCase()}
        </div>

        {/* Nickname */}
        {editing ? (
          <div className="flex items-center justify-center gap-2 mb-4">
            <Input
              value={newNickname}
              onChange={(e) => setNewNickname(e.target.value)}
              className="max-w-[200px] bg-slate-700 border-slate-600 text-white text-center"
              maxLength={20}
            />
            <Button
              onClick={handleSaveNickname}
              disabled={saving}
              size="sm"
              className="bg-teal-500 hover:bg-teal-600"
            >
              <Save className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => {
                setEditing(false);
                setNewNickname(profile.nickname);
              }}
              variant="ghost"
              size="sm"
              className="text-slate-400"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 mb-2">
            <h1 className="text-2xl font-bold text-white">{profile.nickname}</h1>
            <button
              onClick={() => setEditing(true)}
              className="p-1 text-slate-400 hover:text-teal-400 transition-colors"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          </div>
        )}
        <p className="text-slate-400">{profile.email}</p>
      </motion.div>

      {/* Rewards Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <Button
          onClick={() => navigate('/rewards')}
          className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold py-4 rounded-xl"
        >
          <Gift className="w-5 h-5 mr-2" />
          {t('profile.viewRewards', 'View Rewards & Payouts')}
        </Button>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Target, label: t('profile.quizzesPlayed'), value: profile.stats?.quizzes_played || profile.stats?.total_quizzes || 0, color: 'teal' },
          { icon: Trophy, label: t('profile.avgCorrect'), value: `${Number(profile.stats?.avg_correct || profile.stats?.best_score || 0).toFixed(2)}%`, color: 'yellow' },
          { icon: Award, label: t('profile.personalBest'), value: Number(profile.stats?.personal_best || profile.stats?.best_score || 0).toFixed(2), color: 'purple' },
          { icon: Flame, label: t('profile.currentStreak'), value: profile.stats?.current_streak || 0, color: 'orange' }
        ].map((stat, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 text-center"
          >
            <stat.icon className={`w-8 h-8 mx-auto mb-3 text-${stat.color}-400`} />
            <p className="text-2xl font-bold text-white mb-1">{stat.value}</p>
            <p className="text-sm text-slate-400">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Personal Details & Bank Account */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-teal-400" />
            {t('profile.paymentDetails', 'Payment Details')}
          </h2>
          {!editingDetails && (
            <Button
              onClick={() => setEditingDetails(true)}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-teal-400"
            >
              <Edit2 className="w-4 h-4 mr-1" />
              {t('common.edit', 'Edit')}
            </Button>
          )}
        </div>

        {editingDetails ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">{t('profile.firstName', 'First Name')}</Label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t('profile.enterFirstName', 'Enter first name')}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">{t('profile.lastName', 'Last Name')}</Label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t('profile.enterLastName', 'Enter last name')}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">{t('profile.iban', 'Bank Account (IBAN)')}</Label>
              <Input
                value={bankAccount}
                onChange={(e) => setBankAccount(e.target.value)}
                placeholder="SK89 1234 5678 9012 3456 7890"
                className="bg-slate-700 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-500 mt-1">{t('profile.ibanHint', 'Required for receiving reward payouts')}</p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                onClick={() => {
                  setEditingDetails(false);
                  setFirstName(profile.first_name || '');
                  setLastName(profile.last_name || '');
                  setBankAccount(profile.bank_account || '');
                }}
                variant="ghost"
                className="text-slate-400"
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleSaveDetails}
                disabled={saving}
                className="bg-teal-500 hover:bg-teal-600"
              >
                {saving ? <LoadingSpinner className="w-4 h-4" /> : t('common.save', 'Save')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-400">{t('profile.firstName', 'First Name')}</p>
                <p className="text-white">{profile.first_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-400">{t('profile.lastName', 'Last Name')}</p>
                <p className="text-white">{profile.last_name || '-'}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-400">{t('profile.iban', 'Bank Account (IBAN)')}</p>
              <p className="text-white font-mono">{profile.bank_account || t('profile.notSet', 'Not set')}</p>
            </div>
          </div>
        )}
      </motion.div>

      {/* Badges */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50"
      >
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Award className="w-6 h-6 text-yellow-400" />
          {t('profile.badges')}
        </h2>
        
        {badges.length === 0 ? (
          <div className="text-center py-8">
            <Award className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">{t('profile.noBadges')}</p>
            <p className="text-sm text-slate-500">{t('profile.earnBadges')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {badges.map((badge, idx) => (
              <motion.div
                key={badge.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-slate-700/50 rounded-xl p-4 text-center border border-slate-600/50 hover:border-teal-500/50 transition-all"
              >
                <span className="text-4xl mb-2 block">{badge.icon}</span>
                <p className="font-medium text-white text-sm mb-1">{badge.name}</p>
                <p className="text-xs text-slate-400">{badge.description}</p>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Danger zone: account deletion (required by App Store guideline 5.1.1(v)) */}
      {user?.role !== 'admin' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-red-950/20 rounded-2xl p-6 border border-red-900/40"
          data-testid="danger-zone"
        >
          <h2 className="text-lg font-bold text-red-300 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {t('profile.deleteAccount.section')}
          </h2>
          <p className="text-sm text-slate-400 mb-4">{t('profile.deleteAccount.summary')}</p>
          <Button
            variant="outline"
            onClick={() => { setDeleteConfirm(''); setDeleteOpen(true); }}
            className="bg-transparent border-red-700 text-red-300 hover:bg-red-900/40 hover:text-red-200"
            data-testid="delete-account-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {t('profile.deleteAccount.button')}
          </Button>
        </motion.div>
      )}

      <AlertDialog open={deleteOpen} onOpenChange={(open) => { if (!deleting) setDeleteOpen(open); }}>
        <AlertDialogContent className="bg-slate-900 border-slate-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-300">{t('profile.deleteAccount.title')}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300 space-y-2">
              <span className="block">{t('profile.deleteAccount.warning')}</span>
              <span className="block text-amber-300">{t('profile.deleteAccount.rewardsWarning')}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="delete-confirm" className="text-slate-300">
              {t('profile.deleteAccount.typeToConfirm', { word: DELETE_WORD })}
            </Label>
            <Input
              id="delete-confirm"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              autoComplete="off"
              autoCapitalize="characters"
              placeholder={DELETE_WORD}
              className="bg-slate-800 border-slate-600 text-white"
              data-testid="delete-confirm-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} className="bg-slate-800 border-slate-600 text-white hover:bg-slate-700">
              {t('common.cancel')}
            </AlertDialogCancel>
            <Button
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirm.trim().toUpperCase() !== DELETE_WORD}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-account-confirm"
            >
              {deleting ? t('profile.deleteAccount.deleting') : t('profile.deleteAccount.confirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <LegalLinks className="pt-2 pb-4" />
    </div>
  );
}
